import React, { useRef, useEffect } from "react";
import { MapPin, Mail, Laptop, Target, Zap } from "lucide-react";
import { motion } from "framer-motion";

import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";

const Contact = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="contato" className="py-24 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-3xl md:text-[48px] lg:text-[48px] font-bold text-primary leading-tight font-serif">
            Estamos prontos para ajudar!
          </h2>
          <div className="w-24 h-1.5 bg-accent mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-border/50 text-center space-y-6 hover:-translate-y-2 transition-transform duration-500 group"
          >
            <div className="w-32 h-32 flex items-center justify-center mx-auto">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Laptop
                  className="w-20 h-20 text-accent drop-shadow-md"
                  strokeWidth={1.5}
                />
              </motion.div>
            </div>
            <h3 className="text-2xl font-extrabold text-primary">
              Atendimento online
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Possuímos um escritório físico localizado em Sorocaba/SP, mas
              temos uma estrutura moderna, feita para condução de atendimentos à
              distância para todo o Brasil.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-border/50 text-center space-y-6 hover:-translate-y-2 transition-transform duration-500 group"
          >
            <div className="w-32 h-32 flex items-center justify-center mx-auto">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Target
                  className="w-20 h-20 text-accent drop-shadow-md"
                  strokeWidth={1.5}
                />
              </motion.div>
            </div>
            <h3 className="text-2xl font-extrabold text-primary">
              Foco em resultados
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Atuamos há mais de 10 anos no mercado, defendendo mais de 3000
              clientes, com o principal objetivo de levar soluções inteligentes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-border/50 text-center space-y-6 hover:-translate-y-2 transition-transform duration-500 group"
          >
            <div className="w-32 h-32 flex items-center justify-center mx-auto">
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Zap
                  className="w-20 h-20 text-accent drop-shadow-md fill-accent/20"
                  strokeWidth={1.5}
                />
              </motion.div>
            </div>
            <h3 className="text-2xl font-extrabold text-primary">
              Rapidez no seu caso
            </h3>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Trabalhamos na resolução de seus problemas em pouquíssimo tempo,
              sempre com comprometimento no melhor atendimento e agilidade.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mt-8 mb-20 md:mb-24"
        >
          <div className="relative w-full max-w-[340px] md:max-w-[560px] rounded-3xl overflow-hidden shadow-2xl border-4 border-border/50 bg-black flex items-center justify-center">
            <video
              ref={videoRef}
              src="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6ac5366cc478ac55350b21b3.mp4"
              className="w-full h-auto max-h-[80vh] object-contain rounded-3xl"
              autoPlay
              loop
              muted
              playsInline
              preload="none"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="bg-white rounded-[3rem] overflow-hidden shadow-2xl border border-border/50"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-16 space-y-10 bg-primary text-white">
              <div className="space-y-4">
                <h3 className="text-3xl font-extrabold">Fale Conosco</h3>
                <p className="text-white/70 text-base">
                  Utilize nossos canais diretos para entrar em contato.
                </p>
              </div>

              <div className="pt-6 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-[#b18744] p-2.5 rounded-xl">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/70 text-sm">
                    Rua Antonio Ferreira, 24 Campolim - Sorocaba/SP - CEP
                    18.047.636
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-[#b18744] p-2.5 rounded-xl">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/70 text-sm">(15) 99760-6362</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-[#b18744] p-2.5 rounded-xl">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/70 text-sm">
                    atendimento@advpinheiro.com.br
                  </span>
                </div>
              </div>
            </div>

            <div className="h-full min-h-[400px] relative">
              <iframe
                src="https://maps.google.com/maps?q=Rua%20Antonio%20Ferreira%2C%2024%20Campolim%20-%20Sorocaba%2FSP%20-%20CEP%2018.047.636&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                title="Localização Pinheiro Advocacia"
                className="h-full"
              ></iframe>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
